import { useEffect, useState } from "react";
import "react-tooltip/dist/react-tooltip.css";
import StartClinicalServicePopup from "./popups/StartClinicalServicePopup";
import ConfirmInsurancePopup from "./popups/ConfirmInsurancePopup";
import UpdateInsurancePopup from "./popups/UpdateInsurancePopup"; // New import
import ServiceSelection from "./services/service-selection";
import ConfirmationModalNew from "../../common/ConfirmationModalNew";
import { medicationReviewTypes, minorAilments } from "../text/data";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchClinicalServices,
  setClinicalServiceName,
  setClinicalServiceSteps,
  deleteClinicalService,
  deleteMedicationReview,
  updateSystemSessionApplicationName,
} from "../../../redux/modules/clinical-services/actions";
import MinorAilmentSymptomType from "./services/minor-ailment/MinorAilmentSymptomType";
import MedicationReviewSymptomType from "./services/medication-review/medicationReviewSymptomType";
import { fetchNotepad } from "../../../redux/modules/patient/actions";
import { createPatientProvincialInsuranceDetails } from "../../../../src/redux/modules/patient/actions"; // Assuming this action exists
import {
  extractPhoneNumber,
  formatToYYYYMMDDBackend,
} from "../../../helpers/functions";

const ClinicalServices = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { patient } = useSelector((state) => state.patients);
  const { clinicalServiceObj, clinicalServicesSmall } = useSelector((state) => state.clinicalService);
  const dispatch = useDispatch();
  
  // Fetch clinical services on mount to ensure we have the latest data
  useEffect(() => {
    dispatch(fetchClinicalServices(id));
  }, [dispatch, id]);

  const [active, setActive] = useState(null);

  const [selectedService, setSelectedService] = useState(null);
  const [startNewService, setStartNewService] = useState(false);
  
  // Service conflict popup state
  const [showServiceConflictPopup, setShowServiceConflictPopup] = useState(false);
  // `runningService` holds the currently running service (minor ailment OR medication review)
  const [runningService, setRunningService] = useState(null);
  
  const [minorAilmentsStep, setMinorAilmentsStep] = useState(1);
  const [minorAilmentCreated, setMinorAilmentCreated] = useState(false);

  const [showMinorAilmentTooltip, setShowMinorAilmentTooltip] = useState(false);
  const [allowAilmentSelection, setAllowAilmentSelection] = useState(false);
  const [openNoSymptomMatchDialog, setOpenNoSymptomMatchDialog] =
    useState(false);

  // Insurance confirmation popup state
  const [showInsuranceConfirmPopup, setShowInsuranceConfirmPopup] = useState(false);
  const [pendingServiceSelection, setPendingServiceSelection] = useState(null);

  // Update insurance popup state
  const [showUpdateInsurancePopup, setShowUpdateInsurancePopup] = useState(false);

  const [createNewClinicalService, setCreateNewClinicalService] =
    useState(false);

  // Helper function to check for running Minor Ailment services
  const checkForRunningMinorAilment = () => {
    return (clinicalServicesSmall?.minorAilments || []).find(
      (svc) => svc?.status === "inProgress"
    );
  };

  // Helper function to check for running Medication Review services
  const checkForRunningMedicationReview = () => {
    return (clinicalServicesSmall?.medicationReviews || []).find(
      (svc) => svc?.status === "inProgress"
    );
  };

  // Helper function to handle service conflict
  const handleServiceConflict = (service) => {
    // store the running service object (could be a minor ailment or a medication review)
    setRunningService(service);
    setShowServiceConflictPopup(true);
    setStartNewService(false);
    setSelectedService(null);
  };

  // Determine label to show in conflict popup based on running service
  const getRunningServiceLabel = () => {
    if (!runningService) return "Minor Ailment Assessment";
    // If the running service comes from medicationReviews and is annual
    if (runningService.medicationReviewTypeCode === "AnnualMedicationReview") {
      return "Annual Medication Review Assessment";
    }
    // If it's any medication review
    if (runningService.medicationReviewTypeCode) {
      return "Medication Review Assessment";
    }
    // Default to minor ailment
    return "Minor Ailment Assessment";
  };

  // Check if patient has no provincial insurance
  const hasNoProvincialInsurance = () => {
    // Adjust this logic based on how insurance info is stored in your patient object
    // Common field names might be: provincialInsurance, insurance, hasInsurance, etc.
    return !patient?.provincialInsurance || 
           patient?.provincialInsurance === false || 
           patient?.provincialInsurance === 'none' ||
           patient?.noProvincialInsurance === true;
  };

  const onContinue = (step) => {
    if (step == 1) {
      setAllowAilmentSelection(true);
    } else setAllowAilmentSelection(false);
  };

  const handleToggle = (index) => {
    if (active === index) {
      setActive(null);
    } else {
      setActive(index);
    }
  };

  const getActualService = (service) => {
    if (typeof service === "string") {
      return service;
    }
    if (typeof service === "boolean") {
      return selectedService;
    }
    return service || selectedService;
  };

  const getMainMenuLabel = (serviceName) => {
    if (serviceName === "Minor Ailments Prescribing Service") {
      return "Minor Ailments";
    }
    if (serviceName === "Medication Review") {
      return "Medication Reviews";
    }
    return serviceName;
  };

  const startService = (service = selectedService) => {
    const actualService = getActualService(service);
    
    if (!actualService) return;

    dispatch(updateSystemSessionApplicationName(getMainMenuLabel(actualService)));
    
    // Update selectedService if service is a string and different
    if (typeof service === "string" && service !== selectedService) {
      setSelectedService(service);
    }
    
    if (actualService === "Medication Review") {
      const runningMA = checkForRunningMinorAilment();
      if (runningMA) {
        handleServiceConflict(runningMA);
        return;
      }
      const runningMedReview = checkForRunningMedicationReview();
      if (runningMedReview) {
        handleServiceConflict(runningMedReview);
        return;
      }
    }

    if (actualService === "Minor Ailments Prescribing Service") {
      const runningMedReview = checkForRunningMedicationReview();
      if (runningMedReview) {
        handleServiceConflict(runningMedReview);
        return;
      }
      const runningMA = checkForRunningMinorAilment();
      if (runningMA) {
        handleServiceConflict(runningMA);
        return;
      }
    }
    
    // Check insurance and proceed
    if (hasNoProvincialInsurance()) {
      setPendingServiceSelection(actualService);
      setShowInsuranceConfirmPopup(true);
    } else {
      setStartNewService(true);
    }
  };

  const handleInsuranceConfirmation = (option) => {
    setShowInsuranceConfirmPopup(false);
    
    switch (option) {
      case 'consent_to_pay':
        // Patient consents to pay - proceed with service
        setStartNewService(true);
        break;
      case 'does_not_consent':
        // Patient does not consent - cancel service selection
        setSelectedService(null);
        setPendingServiceSelection(null);
        // You might want to show a message or handle this case differently
        break;
      case 'update_insurance':
        // Show the update insurance popup
        setShowUpdateInsurancePopup(true);
        break;
      default:
        break;
    }
    
    setPendingServiceSelection(null);
  };

  const handleUpdateInsuranceSave = (insuranceData) => {
    setShowUpdateInsurancePopup(false);
    
    const provincialInsurance = insuranceData.healthCardNumber && insuranceData.healthCardVersionCode
      ? {
          cardNumber: extractPhoneNumber(insuranceData.healthCardNumber),
          expiryDate: formatToYYYYMMDDBackend(insuranceData.healthCardExpiryDate),
          versionCode: insuranceData.healthCardVersionCode,
          province: "ontario",
        }
      : null;
  
    dispatch(createPatientProvincialInsuranceDetails(patient.id, provincialInsurance));
    
    // After saving, proceed to start the service
    setStartNewService(true);
  };

  const onTreatment = (index) => {
    if (
      active === index &&
      minorAilments[index].title === "Allergic Rhinitis"
    ) {
      return;
    }
  };

  const onReview = (index) => {
    if (active === index && medicationReviewTypes[index].title === "Annual Medication Review") {
      const runningService = checkForRunningMinorAilment();
      if (runningService) {
        handleServiceConflict(runningService);
      } else {
        setCreateNewClinicalService(true);
      }
    }
  };
  
  const handleReturnToCurrentService = () => {
    setShowServiceConflictPopup(false);
    if (runningService) {
      navigate(`/patient/${id}/clinical-services/${runningService.id}`);
    }
    setRunningService(null);
  };
  
  const handleCancelCurrentService = () => {
    setShowServiceConflictPopup(false);
    if (runningService) {
      // If the running service is a medication review, call its delete action
      if (runningService.medicationReviewTypeCode) {
        dispatch(deleteMedicationReview(id, runningService.id, navigate));
      } else {
        dispatch(deleteClinicalService(id, runningService.id, navigate));
      }
      dispatch(fetchClinicalServices(id));
    }
    setRunningService(null);
  };
  // Close popup without cancelling the running service
  const closeServiceConflictPopup = () => {
    setShowServiceConflictPopup(false);
  };
  const onPrevention = () => {};

  useEffect(() => {
    let serviceId = undefined;
    if (clinicalServiceObj !== null && minorAilmentCreated === true) {
      setMinorAilmentCreated(false);
      dispatch(fetchClinicalServices(id));
      serviceId = clinicalServiceObj.id;
      navigate(`/patient/${id}/clinical-services/${serviceId}`);
    }
    var ptId = serviceId === undefined ? id : null;
    dispatch(fetchNotepad(id));
    dispatch(setClinicalServiceName(null));
    dispatch(setClinicalServiceSteps(null));
  }, [clinicalServiceObj]);

  if (!patient) return null;
  return (
    <>
      <div className="bg-gray-20 flex flex-1 w-full">
        <div className="relative flex flex-col w-full gap-4 pt-[30px] bg-white">
          <div className="flex flex-col">
            <div className="flex flex-col gap-5">
              {minorAilmentsStep == 1 && (
                <ServiceSelection
                  setStartNewService={startService}
                  setSelectedService={setSelectedService}
                />
              )}
                {minorAilmentsStep == 2 &&
                selectedService === "Minor Ailments Prescribing Service" && (
                  <MinorAilmentSymptomType
                    setOpenNoSymptomMatchDialog={setOpenNoSymptomMatchDialog}
                    showMinorAilmentTooltip={showMinorAilmentTooltip}
                    setMinorAilmentsStep={setMinorAilmentsStep}
                    setShowMinorAilmentTooltip={setShowMinorAilmentTooltip}
                    setAllowAilmentSelection={setAllowAilmentSelection}
                    openNoSymptomMatchDialog={openNoSymptomMatchDialog}
                    allowAilmentSelection={allowAilmentSelection}
                    setMinorAilmentCreated={setMinorAilmentCreated}
                    active={active}
                    handleToggle={handleToggle}
                    onTreatment={onTreatment}
                    onPrevention={onPrevention}
                    onContinue={onContinue}
                  />
                )}
              {minorAilmentsStep == 2 &&
                selectedService === "Medication Review" && (
                  <MedicationReviewSymptomType
                    createNewClinicalService={createNewClinicalService}
                    setCreateNewClinicalService={setCreateNewClinicalService}
                    setOpenNoSymptomMatchDialog={setOpenNoSymptomMatchDialog}
                    showMinorAilmentTooltip={showMinorAilmentTooltip}
                    setMinorAilmentsStep={setMinorAilmentsStep}
                    setShowMinorAilmentTooltip={setShowMinorAilmentTooltip}
                    setAllowAilmentSelection={setAllowAilmentSelection}
                    openNoSymptomMatchDialog={openNoSymptomMatchDialog}
                    allowAilmentSelection={allowAilmentSelection}
                    setMinorAilmentCreated={setMinorAilmentCreated}
                    active={active}
                    handleToggle={handleToggle}
                    onReview={onReview}
                    onContinue={onContinue}
                  />
                )}
            </div>
          </div>

          {/* Insurance Confirmation Popup */}
          <ConfirmInsurancePopup
            isOpen={showInsuranceConfirmPopup}
            onClose={() => {
              setShowInsuranceConfirmPopup(false);
              setPendingServiceSelection(null);
            }}
            onProceed={handleInsuranceConfirmation}
            patientName={patient?.name}
          />

          {/* Update Insurance Popup */}
          <UpdateInsurancePopup
            isOpen={showUpdateInsurancePopup}
            onClose={() => {
              setShowUpdateInsurancePopup(false);
              setPendingServiceSelection(null);
            }}
            onSaveAndContinue={handleUpdateInsuranceSave}
            patientName={patient?.name}
          />

          {startNewService && ( //Need to implement care-giver here.
            <StartClinicalServicePopup
              title={selectedService}
              subTitle="Start New Service"
              handleClose={(abc) => setStartNewService(false)}
              onNext={() => {
                if (selectedService === "Medication Review") {
                  const runningService = checkForRunningMinorAilment();
                  if (runningService) {
                    handleServiceConflict(runningService);
                    return;
                  }
                }
                setMinorAilmentsStep(2);
                setStartNewService(false);
              }}
            />
          )}

          {/* Service Conflict Popup */}
          <ConfirmationModalNew
            open={showServiceConflictPopup}
            title="Service Already in Progress"
            type="primary"
            titleClassName="!text-[24px]"
            headClassName="px-8 pt-4 pb-4"
            className="!min-w-[600px]"
            actionsClassName="justify-items-center"
            buttonClassName="!w-[240px]"
            content={() => (
              <div className="text-gray-800 text-md font-normal px-2">
                You currently have an ongoing <span className="font-bold">{getRunningServiceLabel()}</span>.
                <br />
                Please <span className="font-bold">complete</span> or <span className="font-bold">cancel</span> the current service before starting a new one.
              </div>
            )}
            confirmText="Return to Current Service"
            cancelText="Cancel Current Service"
            confirmAction={handleReturnToCurrentService}
            cancelAction={handleCancelCurrentService}
            handleClose={closeServiceConflictPopup}
            onCross={closeServiceConflictPopup}
          />
        </div>
      </div>
    </>
  );
};

export default ClinicalServices;